import { useQuery } from '@tanstack/react-query'
import { fetchTopics, type WireTopic } from '../config/topics'
import { fetchRegions, type WireRegion } from '../config/regions'

/**
 * Loads the topic taxonomy from /public/config/topics.json.
 * Cached indefinitely — the list is stable for the lifetime of a deployment.
 * On the production network, ops replaces the JSON file without a rebuild.
 */
export function useTopics() {
  return useQuery<WireTopic[]>({
    queryKey: ['config', 'topics'],
    queryFn: fetchTopics,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

/**
 * Loads the region taxonomy from /public/config/regions.json.
 * Cached indefinitely — the list is stable for the lifetime of a deployment.
 * On the production network, ops replaces the JSON file without a rebuild.
 */
export function useRegions() {
  return useQuery<WireRegion[]>({
    queryKey: ['config', 'regions'],
    queryFn: fetchRegions,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

/** Returns the label for a topic slug, or the slug itself if not found. */
export function topicLabel(topics: WireTopic[], slug: string): string {
  return topics.find(t => t.slug === slug)?.label ?? slug
}

/** Returns the label for a region slug, or the slug itself if not found. */
export function regionLabel(regions: WireRegion[], slug: string): string {
  return regions.find(r => r.slug === slug)?.label ?? slug
}
